import { useEffect } from 'react'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import type { FareClassWithReward } from 'constants/flight'

export interface FlashPackageCharterFallbackPricingResult {
  price: number
  memberPrice?: number
  value?: number
  /**
   * Mirrors `useCharterBundleFallbackPricing`'s own `fallbackHasReturnFareFree`: whether the
   * cheapest jointly-available date this fallback price resolved to happens to have a free MLE→MEL
   * return fare. This is the only source for "Return for FREE with LuxPlus+" while fallback pricing is
   * active (no check-in date) -- `packageView.hasReturnFareFree`/`useOfferPageFlightPrice`'s own
   * offer-wide value both need a resolved dated journey, so they're always empty/false in this
   * exact state even though the real page has its own answer for it here.
   */
  hasReturnFareFree?: boolean
}

interface Props {
  offer: App.HotelOffer
  rooms: Array<App.Occupants>
  charterBundleRoute: App.CharterBundleRoute
  fareClass: FareClassWithReward
  nights: number
  packageUniqueKey: string
  feederOriginAirportCode?: string
  onResolved: (packageUniqueKey: string, result: FlashPackageCharterFallbackPricingResult | undefined) => void
}

/**
 * Renders nothing -- this exists purely to register one package's fallback price with the
 * assistant, not to display anything. Headless, one-per-package mirror of
 * `FlashHotelOptionPriceDetails.tsx`'s own `useCharterBundleFallbackPricing` call: that component
 * calls the hook once per rendered package/rate row (scoped by `packageUniqueKey`), and hooks
 * can't be called in a dynamic loop inside a single component body, so the assistant's source
 * registration mounts one instance of this component per package view instead, and collects
 * results via `onResolved` rather than rendering anything.
 */
export default function FlashPackageCharterFallbackPricingRegistration({
  offer,
  rooms,
  charterBundleRoute,
  fareClass,
  nights,
  packageUniqueKey,
  feederOriginAirportCode,
  onResolved,
}: Props) {
  const { fallbackPricing, fallbackMemberPricing, fallbackHasReturnFareFree } = useCharterBundleFallbackPricing({
    offer,
    rooms,
    charterBundleRoute,
    fareClass,
    nights,
    packageUniqueKey,
    feederOriginAirportCode,
  })

  useEffect(() => {
    onResolved(packageUniqueKey, fallbackPricing ? {
      price: fallbackPricing.price,
      memberPrice: fallbackMemberPricing?.price,
      value: fallbackPricing.value,
      hasReturnFareFree: fallbackHasReturnFareFree,
    } : undefined)
  }, [packageUniqueKey, fallbackPricing, fallbackMemberPricing, fallbackHasReturnFareFree, onResolved])

  // Separate from the effect above (rather than a shared cleanup) so this only clears the entry on
  // a genuine unmount -- collapse, duration change, or fallback mode toggling off -- not on every
  // ordinary price update. The parent always keys this component by `packageUniqueKey`, so a prop
  // change here would already force a remount rather than an in-place update. Without this, a
  // stale price for a since-changed occupancy/duration would survive and be reported as still
  // resolved once this component remounts -- see `isPackagePriceStillResolving` in
  // buildOfferPageSnapshot.ts, which treats any existing entry as resolved.
  useEffect(() => () => onResolved(packageUniqueKey, undefined), [packageUniqueKey, onResolved])

  return null
}
